import {
  HTTP_INTERCEPTORS,
  HttpErrorResponse,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {
  DialogWidth,
  retryWithBackoff,
  SessionService,
  ThemePalette,
  RetryWithBackoffError
} from '@q9elements/ui-kit/common';
import {
  ConfirmModalDialogComponent,
  TwoFaRequiredDialogComponent
} from '@q9elements/ui-kit/components';
import {catchError, EMPTY, switchMap, throwError} from 'rxjs';

import {Q9maCookieService} from '../services/cookie.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly globalErrors = ['user.err.blocked', 'user.err.deleted_from_system'];
  private readonly localErrors = [
    'organization.not_found',
    'user_in_team.blocked',
    'team.err.is_not_pro',
    'err.rights.enterprise_not_active',
    'team_suspended',
    'team.info.is_not_pro',
    'element_not_exists',
    'license_required'
  ];
  private readonly permissionErrors = [
    'user.err.org_admin_rights_needed',
    'user.err.user_is_not_sysadmin',
    'release.err.rights.not_release_admin',
    'team.err.pro_trial.expired',
    'user_does_not_have_permission_to_be_map_owner',
    'folder.err.user_does_not_have_permission_to_be_folder_owner',
    'user_does_not_have_permission_to_be_refmodel_owner',
    'refmodel.err.rights.does_not_exist',
    'user.err.no_have_enough_permission',
    'user.err.org_req_manager_rights_needed',
    'map.err.rights.does_not_exist',
    'map.err_cant_copy'
  ];
  private readonly twoFactorErrors = ['two_factor_required'];

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private sessionService: SessionService,
    private cookieService: Q9maCookieService
  ) {}

  intercept(req: HttpRequest<never>, next: HttpHandler) {
    return next.handle(req).pipe(
      retryWithBackoff(),
      catchError(error => {
        const response =
          req.responseType === 'text' ? {...error, error: JSON.parse(error.error)} : error;

        return this.handlerError(response);
      })
    );
  }

  private handlerError(response: HttpErrorResponse | RetryWithBackoffError) {
    const {
      error,
      status,
      isForbidden,
      isGlobalError,
      isLocalError,
      isPermissionError,
      isUnauthorized,
      isBadRequest,
      isTwoFactorRequired,
      gracePeriodNotified,
      gracePeriodExpired
    } = this.parseErrorResponse(response);

    if (isUnauthorized) {
      return this.logout();
    }

    if (isBadRequest || isPermissionError) {
      return throwError(() => ({error, status}));
    }

    const showTwoFactorDialog =
      isForbidden && isTwoFactorRequired && (gracePeriodExpired || !gracePeriodNotified);

    if (isForbidden && !isTwoFactorRequired) {
      this.sessionService.togglePlaceholder(true);

      if (status === 'license_required') {
        this.showErrorDialog(error)
          .pipe(switchMap(() => this.sessionService.startSession()))
          .subscribe();

        return EMPTY;
      }

      return this.showErrorDialog(error).pipe(
        switchMap(() => {
          if (isGlobalError) {
            return this.logout();
          }

          if (isLocalError) {
            return this.reload();
          }

          return this.logout({error, status});
        })
      );
    } else if (showTwoFactorDialog) {
      this.showTwoFactorDialog(gracePeriodExpired).subscribe(() => window.location.reload());

      return EMPTY;
    }

    if (isLocalError) {
      return throwError(() => ({error, status}));
    }

    return throwError(() => ({error, status}));
  }

  private parseErrorResponse(response: HttpErrorResponse | RetryWithBackoffError) {
    const {error: errorResponse, status: statusCode} = response as HttpErrorResponse;
    const message = response.message || 'Unhandled error';

    const {
      error: errorMessage,
      status: errorStatus,
      http_code,
      gracePeriodExpired = false,
      gracePeriodNotified = false
    } = errorResponse || (response as RetryWithBackoffError).errorMessage;

    const [
      isGlobalError,
      isLocalError,
      isPermissionError,
      isTwoFactorRequired,
      isForbidden,
      isUnauthorized,
      isBadRequest
    ] = [
      this.globalErrors.includes(errorStatus),
      this.localErrors.includes(errorStatus),
      this.permissionErrors.includes(errorStatus),
      this.twoFactorErrors.includes(errorStatus),
      [statusCode, http_code].some(status => status === 403),
      [statusCode, http_code].some(status => status === 401),
      [statusCode, http_code].some(status => status === 400)
    ];

    return {
      error: errorMessage || message,
      status: errorStatus,
      isGlobalError,
      isLocalError,
      isPermissionError,
      isForbidden,
      isUnauthorized,
      isBadRequest,
      isTwoFactorRequired,
      gracePeriodExpired,
      gracePeriodNotified
    };
  }

  private showErrorDialog(message: string) {
    this.dialog.closeAll();

    return this.dialog
      .open(ConfirmModalDialogComponent, {
        width: DialogWidth.SM,
        data: {
          modalTitle: 'GENERAL.ERROR',
          content: message,
          showCancelButton: false,
          confirmText: 'GENERAL.OK',
          color: ThemePalette.WARN
        }
      })
      .afterClosed();
  }

  private showTwoFactorDialog(gracePeriodExpired = false) {
    return this.dialog
      .open(TwoFaRequiredDialogComponent, {
        width: DialogWidth.SM,
        data: {gracePeriodExpired}
      })
      .afterClosed();
  }

  private reload() {
    window.location.href = window.location.origin;

    return EMPTY;
  }

  private logout(error?: {error: string; status: number}) {
    this.cookieService.deleteToken();
    const queryParams: {redirect?: string} = {};

    if (window.location.pathname !== '/') {
      queryParams.redirect = encodeURIComponent(window.location.href);
    }

    this.router.navigate(['/signin'], {
      relativeTo: null,
      queryParams
    });

    return error ? throwError(() => error) : EMPTY;
  }
}

export function provideErrorInterceptor() {
  return {
    provide: HTTP_INTERCEPTORS,
    useClass: ErrorInterceptor,
    multi: true
  };
}
